import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as DB from '@/lib/supabaseDb';
import { useToast } from '@/components/feature/Toast';
import { useDataRefresh } from '@/hooks/useDataRefresh';
import PaymentPromiseFormModal from './components/PaymentPromiseFormModal';
import PaymentPromiseCard from './components/PaymentPromiseCard';
import PaymentFormModal from '@/components/feature/PaymentFormModal';
import { formatTRY } from '@/lib/currencyFormat';
import { STATUS_FILTER_GROUPS } from '@/lib/promiseStatus';

type TabKey = string;

export default function PaymentPromisesPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { version } = useDataRefresh();
  const [searchParams] = useSearchParams();
  const filterParam = searchParams.get('filter');

  const initialTab = filterParam || 'all';
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const [promises, setPromises] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [allSales, setAllSales] = useState<any[]>([]);
  const [promissoryNotes, setPromissoryNotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingPromise, setEditingPromise] = useState<any | null>(null);
  const [showDetailModal, setShowDetailModal] = useState<any | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState<{ customerId: string; saleId: string; amount: number; mode: 'promise' | 'customer' } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Use central RPC for promises with live debt data
      const [promRes, custRes, salesRes, notesRes] = await Promise.all([
        DB.getPaymentPromisesWithCurrentDebt(),
        DB.getCustomers(),
        DB.getSales(),
        DB.getPromissoryNotes(),
      ]);

      setPromises((promRes.data as any[]) || []);
      setCustomers((custRes.data as any[]) || []);
      setAllSales((salesRes.data as any[]) || []);
      setPromissoryNotes((notesRes.data as any[]) || []);
      setError(null);
    } catch {
      setError('Veri yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData, version]);

  // Build lookup maps for backward compatibility (form modals still need them)
  const customerMap = useMemo(() => {
    const map: Record<string, any> = {};
    customers.forEach(c => { map[c.id] = c; });
    return map;
  }, [customers]);

  const saleMap = useMemo(() => {
    const map: Record<string, any> = {};
    allSales.forEach(s => { map[s.id] = s; });
    return map;
  }, [allSales]);

  const noteMap = useMemo(() => {
    const map: Record<string, any> = {};
    promissoryNotes.forEach(n => { map[n.id] = n; });
    return map;
  }, [promissoryNotes]);

  // Get debt source reference display from the enriched promise data
  const getDebtSourceRef = (p: any): string | null => {
    if (p.sale_ref) return `Satış ${p.sale_ref}`;
    if (p.note_ref) return `Senet ${p.note_ref}`;
    if (p.installment_ref) return `Taksit ${p.installment_ref}`;
    if (p.enforcement_ref) return `İcra ${p.enforcement_ref}`;
    if (p.sale_id) return 'Satış (bağlı)';
    if (p.promissory_note_id) return 'Senet (bağlı)';
    return 'Genel Borç';
  };

  // Current customer debt comes directly from the RPC response
  const getCurrentDebt = (promise: any): number => {
    return promise.current_customer_debt ?? 0;
  };

  const filteredPromises = useMemo(() => {
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });

    if (activeTab === 'all') return promises;

    const group = STATUS_FILTER_GROUPS.find(g => g.key === activeTab);
    if (!group || group.statuses.length === 0) return promises;

    // Special handling for time-based filters
    if (activeTab === 'today_call') {
      return promises.filter(p =>
        (p.follow_up_status || 'Bekliyor') === 'Aranacak' &&
        (p.next_follow_up_date === today || p.promised_date === today)
      );
    }
    if (activeTab === 'call_now') {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      return promises.filter(p => {
        if ((p.follow_up_status || 'Bekliyor') !== 'Aranacak') return false;
        if (p.next_follow_up_date !== today && p.promised_date !== today) return false;
        if (!p.next_follow_up_time) return true;
        const [h, m] = (p.next_follow_up_time || '').split(':').map(Number);
        return !isNaN(h) && !isNaN(m) && (h < currentHour || (h === currentHour && m <= currentMinute));
      });
    }

    return promises.filter(p => group.statuses.includes(p.follow_up_status || 'Bekliyor'));
  }, [promises, activeTab]);

  const handleCreate = () => {
    setEditingPromise(null);
    setShowFormModal(true);
  };

  const handleEdit = (promise: any) => {
    setEditingPromise(promise);
    setShowFormModal(true);
  };

  const handleFormSaved = () => {
    setShowFormModal(false);
    setEditingPromise(null);
    loadData();
  };

  const handleTakePayment = (promise: any) => {
    const customer = customerMap[promise.customer_id];
    if (!customer) {
      showToast('Müşteri bulunamadı.', 'error');
      return;
    }
    setPaymentTarget({
      customerId: promise.customer_id,
      saleId: promise.sale_id || '',
      amount: promise.promised_amount || 0,
      // Genel borca bağlı (satış/senet seçilmemiş) sözlerde 'customer' modu
      // kullanılır: kullanıcı ödemeyi hangi satış/senede/plana uygulayacağını
      // standart kaynak seçiciden seçer; 'promise' modu yalnızca eski,
      // belirli bir satışa bağlanmış sözlerde kilitli satış akışını korur.
      mode: promise.sale_id ? 'promise' : 'customer',
    });
    setShowPaymentModal(true);
  };

  const handlePaymentCreated = async () => {
    setShowPaymentModal(false);
    setPaymentTarget(null);
    await loadData();
    showToast('Ödeme kaydedildi ve ilgili ödeme sözleri otomatik güncellendi.', 'success');
  };

  const handleFollowUpStatusChange = async (promiseId: string, payload: {
    follow_up_status: string;
    next_follow_up_date?: string | null;
    next_follow_up_time?: string | null;
    last_contact_note?: string;
    contacted_by?: string;
  }) => {
    setActionLoading(promiseId);
    try {
      const { error: updateErr } = await DB.updatePaymentPromiseFollowUpStatus(promiseId, {
        ...payload,
        last_contacted_at: new Date().toISOString(),
      });
      if (updateErr) {
        showToast(updateErr, 'error');
        return;
      }
      showToast('Takip durumu güncellendi.', 'success');
      await loadData();
    } catch {
      showToast('Durum güncellenirken hata oluştu.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePostponeConfirm = async (promiseId: string, newDate: string, newAmount: number) => {
    setActionLoading(promiseId);
    try {
      const { data: existing } = await DB.getPaymentPromiseById(promiseId);
      if (!existing) { showToast('Ödeme sözü bulunamadı.', 'error'); setActionLoading(null); return; }

      const updates: any = {
        promised_date: newDate,
        promised_amount: newAmount,
        follow_up_status: 'Ertelendi',
        next_follow_up_date: newDate,
      };
      const { error: updErr } = await DB.updatePaymentPromise(promiseId, updates);
      if (updErr) { showToast(updErr, 'error'); setActionLoading(null); return; }

      if (!(existing as any).original_promise_date) {
        await DB.updatePaymentPromise(promiseId, {
          original_promise_date: (existing as any).promised_date,
        });
      }

      await DB.createPaymentPromiseActivity({
        payment_promise_id: promiseId,
        customer_id: (existing as any).customer_id,
        activity_type: 'Ertelendi',
        activity_date: new Date().toISOString().split('T')[0],
        note: `Tarih ertelendi: ${(existing as any).promised_date} → ${newDate}, Tutar: ${formatTRY((existing as any).promised_amount)} → ${formatTRY(newAmount)}`,
        old_promised_date: (existing as any).promised_date,
        new_promised_date: newDate,
        old_amount: (existing as any).promised_amount,
        new_amount: newAmount,
        created_by: 'Admin',
      });

      showToast('Ödeme sözü ertelendi.', 'success');
      await loadData();
    } catch {
      showToast('Erteleme sırasında hata oluştu.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddActivity = async (promiseId: string, note: string) => {
    const { data: existing } = await DB.getPaymentPromiseById(promiseId);
    if (!existing) { showToast('Ödeme sözü bulunamadı.', 'error'); return; }

    const { error: actErr } = await DB.createPaymentPromiseActivity({
      payment_promise_id: promiseId,
      customer_id: (existing as any).customer_id,
      activity_type: 'Görüşme',
      activity_date: new Date().toISOString().split('T')[0],
      note,
      created_by: 'Admin',
    });

    if (actErr) {
      showToast(actErr, 'error');
      return;
    }
    showToast('Not eklendi.', 'success');
  };

  const handleDelete = async (promiseId: string) => {
    setActionLoading(promiseId);
    try {
      const { error: delErr } = await DB.deletePaymentPromise(promiseId);
      if (delErr) { showToast(delErr, 'error'); setActionLoading(null); return; }
      showToast('Ödeme sözü silindi.', 'success');
      setShowDeleteConfirm(null);
      await loadData();
    } catch {
      showToast('Silme sırasında hata oluştu.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const activeGroup = STATUS_FILTER_GROUPS.find(g => g.key === activeTab);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin mb-4"></div>
        <h2 className="text-lg font-semibold text-foreground-700">Yükleniyor...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4"><i className="ri-error-warning-line text-2xl text-red-400"></i></div>
        <h2 className="text-lg font-semibold text-foreground-700">Yükleme başarısız</h2>
        <p className="text-sm text-foreground-400 mt-1">{error}</p>
        <button onClick={loadData} className="btn-primary mt-4 cursor-pointer flex items-center gap-2"><i className="ri-refresh-line"></i>Tekrar Dene</button>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Ödeme Sözleri</h2>
          <p className="text-sm text-foreground-400 mt-0.5">Müşteri ödeme taahhütleri ve takip durumları</p>
        </div>
        <button onClick={handleCreate} className="btn-primary flex items-center gap-2 cursor-pointer whitespace-nowrap">
          <i className="ri-add-line"></i>Yeni Ödeme Sözü
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="bg-background-100 rounded-xl p-1 overflow-x-auto scrollbar-none">
        <div className="flex items-center gap-1 min-w-max">
          {STATUS_FILTER_GROUPS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap cursor-pointer active:scale-95 ${
                activeTab === tab.key
                  ? 'bg-background-50 text-foreground-900 shadow-sm'
                  : 'text-foreground-400 hover:text-foreground-600'
              }`}
              style={{ minHeight: 36 }}
            >
              <i className={`${tab.icon} ${activeTab === tab.key ? 'text-primary-500' : ''}`}></i>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Active filter indicator */}
      {activeGroup && activeTab !== 'all' && (
        <div className="flex items-center gap-2 text-sm text-foreground-500">
          <i className={`${activeGroup.icon} text-foreground-400`}></i>
          <span>Filtre: <strong>{activeGroup.label}</strong></span>
          <span className="text-foreground-300">· {filteredPromises.length} sonuç</span>
        </div>
      )}

      {/* Content */}
      <div className="space-y-3">
        {filteredPromises.length === 0 ? (
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-10 flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center">
              <i className="ri-hand-heart-line text-2xl text-foreground-300"></i>
            </div>
            <p className="text-sm text-foreground-400">Bu kategoride ödeme sözü bulunamadı</p>
            <button onClick={handleCreate} className="btn-outline text-sm cursor-pointer">Yeni Ekle</button>
          </div>
        ) : (
          filteredPromises.map((promise) => {
            const customer = customerMap[promise.customer_id];
            const ref = getDebtSourceRef(promise);
            const isActionLoading = actionLoading === promise.id;

            return (
              <PaymentPromiseCard
                key={promise.id}
                promise={promise}
                customer={customer}
                reference={ref}
                customerDebt={getCurrentDebt(promise)}
                sourceRemaining={promise.source_remaining_amount ?? null}
                isLoading={isActionLoading}
                activities={[]}
                onEdit={() => handleEdit(promise)}
                onView={() => setShowDetailModal(promise)}
                onTakePayment={() => handleTakePayment(promise)}
                onFollowUpStatusChange={(payload) => handleFollowUpStatusChange(promise.id, payload)}
                onPostpone={(newDate, newAmount) => handlePostponeConfirm(promise.id, newDate, newAmount)}
                onAddActivity={(note) => handleAddActivity(promise.id, note)}
                onDelete={() => setShowDeleteConfirm(promise.id)}
                onNavigate={(path) => navigate(path)}
              />
            );
          })
        )}
      </div>

      {/* Create / Edit Modal */}
      <PaymentPromiseFormModal
        open={showFormModal}
        onClose={() => { setShowFormModal(false); setEditingPromise(null); }}
        onSaved={handleFormSaved}
        editData={editingPromise}
        customers={customers}
        allSales={allSales}
        promissoryNotes={promissoryNotes}
      />

      {/* Detail Modal */}
      {showDetailModal && (
        <PaymentPromiseFormModal
          open={!!showDetailModal}
          onClose={() => setShowDetailModal(null)}
          onSaved={handleFormSaved}
          editData={showDetailModal}
          customers={customers}
          allSales={allSales}
          promissoryNotes={promissoryNotes}
          readOnly
        />
      )}

      {/* Payment Modal */}
      {paymentTarget && (
        <PaymentFormModal
          open={showPaymentModal}
          onClose={() => { setShowPaymentModal(false); setPaymentTarget(null); }}
          mode={paymentTarget.mode}
          customerId={paymentTarget.customerId}
          saleId={paymentTarget.saleId || undefined}
          initialAmount={paymentTarget.amount}
          onPaymentCreated={handlePaymentCreated}
        />
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[70] animate-fade-in" onClick={() => setShowDeleteConfirm(null)} />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-[71] max-w-md mx-auto bg-background-50 rounded-2xl p-6 shadow-xl animate-slide-up">
            <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-2xl text-red-500"></i>
            </div>
            <h3 className="text-lg font-semibold text-foreground-900 text-center">Ödeme Sözünü Sil</h3>
            <p className="text-sm text-foreground-500 text-center mt-2">Bu ödeme sözünü silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</p>
            <div className="flex items-center gap-3 mt-6">
              <button onClick={() => setShowDeleteConfirm(null)} className="flex-1 btn-outline cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={() => handleDelete(showDeleteConfirm)} className="flex-1 bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium cursor-pointer hover:bg-red-600 whitespace-nowrap">
                Sil
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}